import { useState } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";

interface JsonTreeProps {
  value: unknown;
  initiallyExpanded?: boolean;
  depth?: number;
  rootKey?: string;
  /** Top-level keys to highlight (rendered with the indigo chip). */
  highlightKeys?: ReadonlySet<string>;
}

const DEFAULT_HIGHLIGHT = new Set(["fielditem", "lineitem", "llm"]);

/**
 * Recursive JSON tree viewer used by the layout detail and verification
 * panels. Strings render in emerald, numbers in violet, booleans in
 * amber, and the listed top-level keys get an indigo highlight.
 */
export function JsonTree({
  value,
  initiallyExpanded = false,
  depth = 0,
  rootKey,
  highlightKeys = DEFAULT_HIGHLIGHT,
}: JsonTreeProps): React.ReactElement {
  const [expanded, setExpanded] = useState(initiallyExpanded || depth < 1);

  if (value === null || value === undefined) {
    return <span className="text-gray-400">null</span>;
  }
  if (typeof value === "string") {
    return (
      <span className="text-emerald-700 break-all">"{value}"</span>
    );
  }
  if (typeof value === "number") {
    return <span className="tabular-nums text-violet-700">{value}</span>;
  }
  if (typeof value === "boolean") {
    return <span className="text-amber-700">{String(value)}</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400">[]</span>;
    return (
      <div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center text-gray-700 hover:text-gray-900"
        >
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          <span className="text-gray-500">
            [{value.length} item{value.length === 1 ? "" : "s"}]
          </span>
        </button>
        {expanded ? (
          <ul className="ml-4 border-l border-gray-200 pl-3">
            {value.map((item, i) => (
              <li key={i} className="my-0.5">
                <span className="mr-1 text-gray-400 tabular-nums">{i}:</span>
                <JsonTree
                  value={item}
                  depth={depth + 1}
                  highlightKeys={highlightKeys}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <span className="text-gray-400">{"{}"}</span>;
    return (
      <div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center text-gray-700 hover:text-gray-900"
        >
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          <span className="text-gray-500">
            {"{"}
            {entries.length} key{entries.length === 1 ? "" : "s"}
            {"}"}
          </span>
        </button>
        {expanded ? (
          <ul className="ml-4 border-l border-gray-200 pl-3">
            {entries.map(([k, v]) => {
              const highlight = depth === 0 && rootKey === undefined && highlightKeys.has(k);
              return (
                <li key={k} className="my-0.5">
                  <span
                    className={`mr-1 ${
                      highlight
                        ? "rounded bg-indigo-100 px-1 font-semibold text-indigo-800"
                        : "text-gray-700"
                    }`}
                  >
                    {k}:
                  </span>
                  <JsonTree
                    value={v}
                    depth={depth + 1}
                    rootKey={k}
                    highlightKeys={highlightKeys}
                  />
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    );
  }

  return <span>{String(value)}</span>;
}
